import { EVENT_TYPE_LABEL } from './eventTypeLabels';

// Client responses can include historical, free-form descriptions created
// before the client API stopped returning pricing implementation metadata.
// Keep the charged service visible while removing the parts that explain how
// the charge was calculated.
const INTERNAL_PRICING_REFERENCE =
  /\b(?:rate[\s_-]*card(?:\s*(?:field|rule))?|rateCardField|ruleSnapshot|pricing[\s_-]*rule|pricingRule|rate[\s_-]*source|rateSource|per[\s_-]*case[\s_-]*rate|perCaseRate|case[\s_-]*pack(?:ing)?[\s_-]*(?:rate|pricing)|(?:raw\s+)?(?:carrier|shipping|packaging|insurance|label|vendor|material)\s*(?:cost|price|charge|premium|spend)|cost\s*basis|label[\s_-]*cost(?:Dollars)?|marginPct|markupPct)\b/i;

const MARKUP_FRAGMENT =
  /(?:\b(?:carrier|shipping|packaging|insurance)\s*)?\b(?:margin|markup)(?:\s*(?:pct|percentage|rate))?\s*(?::|=|of)?\s*\$?\d+(?:\.\d+)?\s*%(?:\s*of\s*\$?\d+(?:\.\d+)?)?|\b\d+(?:\.\d+)?\s*%\s*(?:shipping\s*)?(?:margin|markup)\b/gi;

const CLIENT_FEE_LABELS = {
  ...EVENT_TYPE_LABEL,
  Ship: 'Shipping',
  ShippingAdjustment: 'Shipping Adjustment',
};

const tidy = (value) =>
  String(value ?? '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s*(?:[—–-]\s*)+$/g, '')
    .replace(/\s*([,;:])\s*$/g, '')
    .replace(/\(\s*\)/g, '')
    .replace(/\s+(?:with|at|via|using)\s*$/i, '')
    .trim();

export const clientSafeFeeLabel = (eventType) => CLIENT_FEE_LABELS[eventType] ?? 'Service';

export const clientSafeFeeTitle = (eventType, title) => {
  if (CLIENT_FEE_LABELS[eventType]) return clientSafeFeeLabel(eventType);
  const value = tidy(title);
  return value && !INTERNAL_PRICING_REFERENCE.test(value) ? value : 'Service Fees';
};

export const clientSafeFeeDescription = (description, eventType) => {
  const fallback = `${clientSafeFeeLabel(eventType)} fee`;
  const raw = tidy(description);
  if (!raw) return fallback;

  // Field names, rule snapshots, rate sources, and JSON-like descriptions are
  // implementation details. Do not attempt to preserve a partial value that
  // could still disclose a rate-card rule.
  if (INTERNAL_PRICING_REFERENCE.test(raw)) return fallback;

  const safe = tidy(
    raw.replace(/\([^)]*(?:margin|markup)[^)]*\)/gi, '').replace(MARKUP_FRAGMENT, ''),
  );

  return safe || fallback;
};

const copyDefined = (source, keys) =>
  keys.reduce((result, key) => {
    if (source?.[key] != null) result[key] = source[key];
    return result;
  }, {});

const toClientSafeTransfer = (transfer) => {
  if (!transfer || typeof transfer !== 'object') return undefined;
  const safeTransfer = copyDefined(transfer, [
    'friendlyId',
    'externalId',
    'originWarehouseName',
    'destinationWarehouseName',
    'leg',
  ]);
  return Object.keys(safeTransfer).length > 0 ? safeTransfer : undefined;
};

// Keep this DTO explicit. Fee objects originate in billing models that may
// contain rate-card fields, raw costs, rule snapshots, or arbitrary metadata.
// An allowlist prevents those fields from leaking into a client-safe display
// object when the backend is serving a legacy payload.
export const toClientSafeFeeLineItem = (item) => {
  const safeItem = copyDefined(item, [
    'id',
    'feeId',
    'referenceId',
    'displayId',
    'shipmentId',
    'createdAt',
    'billedAt',
    'qty',
    'feeApplied',
  ]);
  const transfer = toClientSafeTransfer(item?.transfer);

  return {
    ...safeItem,
    eventType: clientSafeFeeLabel(item?.eventType),
    description: clientSafeFeeDescription(item?.description, item?.eventType),
    ...(transfer ? { transfer } : {}),
  };
};

const numericValue = (value) => {
  if (value == null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

// A billed shipment produces separate Ship rows for the carrier charge and
// the 3PL's margin. Clients only need the final amount, so keep one
// customer-facing row for all Ship rows in the current fee scope. The caller
// applies this helper independently to each order-level or shipment group.
export const toClientSafeFeeLineItems = (items) => {
  const safeItems = [];
  let shippingIndex = -1;

  for (const item of items ?? []) {
    const safeItem = toClientSafeFeeLineItem(item);
    if (item?.eventType === 'Ship') {
      if (shippingIndex === -1) {
        shippingIndex = safeItems.length;
        safeItems.push(
          /\b(?:margin|markup)\b/i.test(safeItem.description ?? '')
            ? { ...safeItem, description: 'Shipping fee' }
            : safeItem,
        );
        continue;
      }

      const first = safeItems[shippingIndex];
      const firstAmount = numericValue(first.feeApplied);
      const nextAmount = numericValue(safeItem.feeApplied);
      const aggregate = {
        ...(first.shipmentId != null ? { shipmentId: first.shipmentId } : {}),
        ...(first.createdAt != null ? { createdAt: first.createdAt } : {}),
        ...(first.billedAt != null ? { billedAt: first.billedAt } : {}),
        ...(firstAmount != null || nextAmount != null
          ? { feeApplied: (firstAmount ?? 0) + (nextAmount ?? 0) }
          : {}),
        eventType: clientSafeFeeLabel('Ship'),
        description: 'Shipping fee',
      };
      safeItems[shippingIndex] = aggregate;
    } else {
      safeItems.push(safeItem);
    }
  }

  return safeItems;
};

const feeLineScopeKey = (item) => {
  const shipmentId = item?.shipmentId ?? null;
  const referenceId = item?.referenceId ?? item?.orderId ?? null;
  if (shipmentId == null && referenceId == null) return null;
  return JSON.stringify([shipmentId, referenceId]);
};

// Invoice event-type groups can contain rows from many orders and shipments.
// Merge Ship components only when their public scope identity matches, and
// retain the first row's safe reference fields so order links still work.
export const toClientSafeScopedFeeLineItems = (items = []) => {
  const safeItems = [];
  const shippingByScope = new Map();

  items.forEach((item) => {
    const safeItem = toClientSafeFeeLineItem(item);
    if (item?.eventType !== 'Ship') {
      safeItems.push(safeItem);
      return;
    }

    const scopeKey = feeLineScopeKey(item);
    if (scopeKey == null) {
      safeItems.push(safeItem);
      return;
    }

    const existingIndex = shippingByScope.get(scopeKey);
    if (existingIndex == null) {
      shippingByScope.set(scopeKey, safeItems.length);
      safeItems.push(safeItem);
      return;
    }

    const existing = safeItems[existingIndex];
    const existingAmount = numericValue(existing.feeApplied);
    const nextAmount = numericValue(safeItem.feeApplied);
    safeItems[existingIndex] = {
      ...existing,
      eventType: clientSafeFeeLabel('Ship'),
      description: 'Shipping fee',
      ...(existingAmount != null || nextAmount != null
        ? { feeApplied: (existingAmount ?? 0) + (nextAmount ?? 0) }
        : {}),
    };
  });

  return safeItems;
};

const ledgerShippingScopeKey = (row) => {
  const meta = row?.meta && typeof row.meta === 'object' ? row.meta : {};
  const invoiceId = row?.invoiceId ?? meta.invoiceId ?? null;
  const invoiceGroupId =
    row?.invoiceGroupId ??
    row?.billingGroupId ??
    meta.invoiceGroupId ??
    meta.billingGroupId ??
    null;
  const shipmentId = row?.shipmentId ?? meta.shipmentId ?? null;
  const referenceId = row?.referenceId ?? row?.orderId ?? null;

  // A legacy row with no order or shipment identity cannot safely be merged
  // with another shipping row. Keep it visible as its own billing scope.
  if (shipmentId == null && referenceId == null) return null;

  return JSON.stringify([invoiceId, invoiceGroupId, shipmentId, referenceId]);
};

// Ledger rows are flat, so apply the same customer-safe merge without crossing
// invoice, order, or shipment boundaries. Unlike the generic fee-line helper,
// this preserves the first row's public ledger fields (reference, invoice
// status, and date) so the existing ledger links and scope indicators continue
// to work. The underlying rows and server-provided totals are not changed.
export const toClientSafeLedgerRows = (rows = []) => {
  const safeRows = [];
  const shippingByScope = new Map();

  rows.forEach((row) => {
    if (row?.eventType !== 'Ship') {
      safeRows.push(row);
      return;
    }

    const safeRow = {
      ...row,
      eventType: clientSafeFeeLabel('Ship'),
      description: clientSafeFeeDescription(row?.description, 'Ship'),
    };

    const scopeKey = ledgerShippingScopeKey(row);
    if (scopeKey == null) {
      safeRows.push(safeRow);
      return;
    }

    const existingIndex = shippingByScope.get(scopeKey);
    if (existingIndex == null) {
      shippingByScope.set(scopeKey, safeRows.length);
      safeRows.push(safeRow);
      return;
    }

    const existing = safeRows[existingIndex];
    const existingAmount = numericValue(existing.feeApplied);
    const nextAmount = numericValue(safeRow.feeApplied);
    safeRows[existingIndex] = {
      ...existing,
      eventType: clientSafeFeeLabel('Ship'),
      description: 'Shipping fee',
      ...(existingAmount != null || nextAmount != null
        ? { feeApplied: (existingAmount ?? 0) + (nextAmount ?? 0) }
        : {}),
    };
  });

  return safeRows;
};

const parseCsv = (value) => {
  const rows = [];
  let row = [];
  let cell = '';
  let quoted = false;

  for (let i = 0; i < value.length; i += 1) {
    const char = value[i];
    if (quoted) {
      if (char === '"' && value[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (char === '"') {
        quoted = false;
      } else {
        cell += char;
      }
    } else if (char === '"') {
      quoted = true;
    } else if (char === ',') {
      row.push(cell);
      cell = '';
    } else if (char === '\n') {
      row.push(cell.replace(/\r$/, ''));
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += char;
    }
  }

  if (cell || row.length) {
    row.push(cell.replace(/\r$/, ''));
    rows.push(row);
  }
  return rows;
};

const toCsv = (rows) =>
  rows
    .map((row) => row.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\r\n');

// This is defense in depth for the client app. The API must still serialize
// only safe columns and descriptions, because a direct request can bypass the
// browser entirely. Keep the billed total but drop per-unit pricing and
// sanitize each description before triggering the download.
export const clientSafeLedgerCsv = (csv) => {
  const [header = [], ...rows] = parseCsv(String(csv ?? ''));
  const column = Object.fromEntries(
    header.map((name, index) => [name.trim().toLowerCase(), index]),
  );
  const eventTypeIndex = column['event type'];
  const descriptionIndex = column.description;
  const allowedColumns = [
    ['ledger id', 'Ledger ID'],
    ['date', 'Date'],
    ['event type', 'Event Type'],
    ['reference', 'Reference'],
    ['description', 'Description'],
    ['qty', 'Qty'],
    ['fee applied', 'Fee Applied'],
    ['invoiced', 'Invoiced'],
    ['invoice id', 'Invoice ID'],
  ].filter(([key]) => column[key] != null);

  const redactedRows = rows.map((row) => {
    const next = [...row];
    if (descriptionIndex != null) {
      next[descriptionIndex] = clientSafeFeeDescription(
        next[descriptionIndex],
        eventTypeIndex != null ? next[eventTypeIndex] : null,
      );
    }
    if (eventTypeIndex != null) next[eventTypeIndex] = clientSafeFeeLabel(next[eventTypeIndex]);
    return next;
  });

  const referenceIndex = column.reference;
  const invoiceIdIndex = column['invoice id'];
  const feeAppliedIndex = column['fee applied'];
  const shippingByScope = new Map();
  const visibleRows = [];

  redactedRows.forEach((row) => {
    if (eventTypeIndex == null || row[eventTypeIndex] !== 'Shipping') {
      visibleRows.push(row);
      return;
    }

    const reference = referenceIndex != null ? row[referenceIndex] : '';
    if (!reference) {
      visibleRows.push(row);
      return;
    }

    const invoiceId = invoiceIdIndex != null ? row[invoiceIdIndex] : '';
    const scopeKey = JSON.stringify([invoiceId, reference]);
    const existingIndex = shippingByScope.get(scopeKey);
    if (existingIndex == null) {
      shippingByScope.set(scopeKey, visibleRows.length);
      visibleRows.push(row);
      return;
    }

    const existing = visibleRows[existingIndex];
    if (descriptionIndex != null) existing[descriptionIndex] = 'Shipping fee';
    if (feeAppliedIndex != null) {
      const existingAmount = numericValue(existing[feeAppliedIndex]);
      const nextAmount = numericValue(row[feeAppliedIndex]);
      if (existingAmount != null || nextAmount != null) {
        const decimalPlaces = [existing[feeAppliedIndex], row[feeAppliedIndex]].reduce(
          (maximum, value) => {
            const decimals = String(value ?? '').split('.')[1]?.length ?? 0;
            return Math.max(maximum, decimals);
          },
          2,
        );
        existing[feeAppliedIndex] = ((existingAmount ?? 0) + (nextAmount ?? 0)).toFixed(
          decimalPlaces,
        );
      }
    }
  });

  return toCsv([
    allowedColumns.map(([, label]) => label),
    ...visibleRows.map((row) => allowedColumns.map(([key]) => row[column[key]])),
  ]);
};
